/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { WorkflowRunState } from '@smartsheet-bridge/bridge-sdk/lib/http';
import {
  Spacer,
  colors,
  sizes,
  usePagination,
  Pagination,
  TypeRampV2,
} from '@smartsheet/lodestar-core';
import { PaginationArrowRightIcon } from '@smartsheet/lodestar-icons';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from '../../../../../../i18n';
import { setshowRunStepsPanel } from '../../../../../../services/ducks/designerDuck/designerDuck';
import {
  allRunsSelector,
  fromRunSelector,
  setFromRun,
  totalRunsSelector,
  UIWorkflowRunState,
  WorkflowRun,
} from '../../../../../../services/ducks/workflowDuck/workflowDuck';
import { setSelectedRunId } from '../../../../../../services/ducks/workflowRunDuck/workflowRunDuck';
import { getVerticalSpacingAroundRunLogList } from '../../../../../../utils/dimensions';
import { Column } from '../../../../../common/Column';
import { CompletedIcon } from '../../../../../icons/CompletedIcon';
import { InProgressIcon } from '../../../../../icons/InProgressIcon';
import { StopIcon } from '../../../../../icons/StopIcon';

const { t: translate } = i18n;

const runLogListContainer = css`
  margin-bottom: ${sizes.small}px;
  height: calc(100vh - ${getVerticalSpacingAroundRunLogList()}px);
  overflow-y: scroll;
`;

const runLogListItemStyle = css`
  justify-content: space-between;
  align-items: center;
  padding: 0px 10px;
  cursor: pointer;
  border-radius: ${sizes.xSmall}px;
  height: 40px;
  :hover {
    background: ${colors.neutralLight10};
  }
`;

const paginationBlockContainer = css`
  float: right;
  padding-bottom: ${sizes.large}px;
`;

const statusIcons = {
  0: <InProgressIcon />,
  100: <InProgressIcon />,
  110: <InProgressIcon />,
  200: <StopIcon />,
  210: <StopIcon />,
  300: <CompletedIcon />,
};

const convertWorkflowRunStateToUI = (
  state: WorkflowRunState
): UIWorkflowRunState => {
  switch (state) {
    case WorkflowRunState.AWAITING_USER_INPUT:
      return UIWorkflowRunState.WAITING_ON_CHATBOT_REPLY;
    case WorkflowRunState.AWAITING_ADMIN_INPUT:
      return UIWorkflowRunState.STOPPED_BY_ERROR;
    case WorkflowRunState.ON_HOLD:
      return UIWorkflowRunState.WAITING;
    case WorkflowRunState.STOPPED:
      return UIWorkflowRunState.STOPPED;
    case WorkflowRunState.DONE:
      return UIWorkflowRunState.DONE;
    default:
    case WorkflowRunState.IN_PROGRESS:
      return UIWorkflowRunState.IN_PROGRESS;
  }
};

export const getRunStateText = (state: UIWorkflowRunState): string => {
  switch (state) {
    case UIWorkflowRunState.DONE:
      return translate('designer:runLog.completed');
    case UIWorkflowRunState.STOPPED_BY_ERROR:
      return translate('designer:runLog.stoppedByError');
    case UIWorkflowRunState.STOPPED:
      return translate('designer:runLog.stopped');
    case UIWorkflowRunState.WAITING_ON_CHATBOT_REPLY:
      return translate('designer:runLog.runningInProgress');
    case UIWorkflowRunState.WAITING:
      return translate('designer:runLog.runningInProgress');
    case UIWorkflowRunState.IN_PROGRESS:
    default:
      return translate('designer:runLog.runningInProgress');
  }
};

const getWorkflowRuns = (
  currentPage: number,
  maxItems: number,
  workflowRuns: WorkflowRun[],
  selectHandler: (id: string) => void
) => {
  const runsToDisplay: any[] = [];
  const end = currentPage * maxItems;
  const start = end - maxItems;

  workflowRuns.slice(start, end).forEach((run: WorkflowRun) => {
    const createTime = new Date(run.createTime / 1000000);
    runsToDisplay.push(
      <Spacer
        orientation="row"
        space="small"
        css={runLogListItemStyle}
        key={run.objectUUID}
        onClick={() => selectHandler(run.objectUUID)}
        clientId={'run'}
        data-test-key={run.objectUUID}
      >
        <Column>
          {statusIcons[convertWorkflowRunStateToUI(run.state)]}
          <TypeRampV2 system="lodestar" variant="bodyStrong">
            {getRunStateText(convertWorkflowRunStateToUI(run.state))}
          </TypeRampV2>
        </Column>
        <Column
          css={css`
            justify-content: flex-end;
          `}
        >
          <TypeRampV2 system="lodestar" variant="bodyStrong">
            {createTime.toLocaleDateString()}{' '}
            {createTime.toLocaleTimeString('en-US', {
              hour12: true,
              hour: '2-digit',
              minute: '2-digit',
            })}
          </TypeRampV2>
          <PaginationArrowRightIcon />
        </Column>
      </Spacer>
    );
  });

  return runsToDisplay;
};

export const List = ({ maxRecords }: { maxRecords: number }) => {
  const dispatch = useDispatch();
  const fromRun = useSelector(fromRunSelector);
  const [currentPage, setCurrentPage] = useState(
    Math.floor(fromRun / maxRecords) + 1
  );
  const allRuns = useSelector(allRunsSelector);
  const totalRuns = useSelector(totalRunsSelector);

  const onPrevClick = () => {
    let prev = fromRun - maxRecords;
    if (prev < 0) {
      const pages = Math.ceil(totalRuns / maxRecords);
      prev = (pages - 1) * maxRecords;
    }
    dispatch(setFromRun(prev));
  };
  const onNextClick = () => {
    let next = fromRun + maxRecords;
    if (next > totalRuns - 1) next = 0;
    dispatch(setFromRun(next));
  };

  const selectHandler = (id: string) => {
    dispatch(setSelectedRunId(id));
    dispatch(setshowRunStepsPanel(true));
  };

  const { getPaginationProps } = usePagination({
    currentPage,
    entitiesPerPage: maxRecords,
    includeFirstAndLastPage: false,
    locale: 'en',
    onChange: setCurrentPage,
    total: totalRuns,
  });

  return (
    <React.Fragment>
      <div css={runLogListContainer} data-pa11y-id="pa11y-runlog-body">
        {getWorkflowRuns(currentPage, maxRecords, allRuns, selectHandler)}
      </div>
      <div css={paginationBlockContainer}>
        <Pagination {...getPaginationProps({ onNextClick, onPrevClick })} />
      </div>
    </React.Fragment>
  );
};
